/**
 * Contact profile popup — the small "tap the avatar to see who this is" card
 * both WhatsApp and Instagram show. Opened from the chat header avatar/name.
 *
 * Design intent: lead with a large, SQUARE, edge-to-edge photo — the way
 * Instagram's own profile-photo viewer and WhatsApp's contact-info screen both
 * do it — not a small circular avatar floating in a padded gradient band
 * (the first version of this dialog, which read as a generic "card" rather
 * than a profile viewer).
 *
 * Instagram threads get the richer info (handle, verified tick, followers,
 * and the follow-relationship both ways) from the User Profile API
 * enrichment. WhatsApp threads show only what that channel exposes — a name
 * and phone number — because the WhatsApp Cloud API returns no customer photo
 * or social graph (see the channel research). A field that came back
 * `undefined` (enrichment not granted to the app) is simply hidden, rather
 * than shown as a wrong "0" or "No".
 */
import { Avatar, Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  VerifiedRounded,
  HowToRegRounded,
  PersonAddAlt1Rounded,
  PhoneOutlined,
} from '@mui/icons-material';
import InstagramLogo from '@/components/common/InstagramLogo';

const INK = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#e5e7eb';

export interface ContactProfileInfo {
  channel?: 'whatsapp' | 'instagram' | string | undefined;
  customerName?: string | undefined;
  username?: string | undefined;
  customerPhone?: string | undefined;
  profilePictureUrl?: string | undefined;
  followerCount?: number | undefined;
  isUserFollowBusiness?: boolean | undefined;
  isBusinessFollowUser?: boolean | undefined;
  isVerifiedUser?: boolean | undefined;
}

function formatCount(n?: number): string {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 >= 100_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 >= 100 ? 1 : 0)}K`;
  return n.toLocaleString();
}

/** One relationship fact — shown only when it's actually known (undefined
 *  means the enrichment wasn't available, not "no"). A plain label + value
 *  row, matching how the stat below it reads, rather than a chip. */
function FactRow({
  known,
  active,
  activeLabel,
  inactiveLabel,
  icon,
}: {
  known: boolean;
  active?: boolean | undefined;
  activeLabel: string;
  inactiveLabel: string;
  icon: React.ReactNode;
}) {
  if (!known) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: active ? '#16a34a' : '#94a3b8', display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: active ? INK : MUTED, fontWeight: active ? 600 : 500 }}>
        {active ? activeLabel : inactiveLabel}
      </Typography>
    </Stack>
  );
}

export default function ContactProfileDialog({
  open,
  onClose,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  contact: ContactProfileInfo | null;
}) {
  if (!contact) return null;
  const isInstagram = contact.channel === 'instagram';
  const displayName = contact.customerName || (contact.username ? `@${contact.username}` : 'Contact');
  const initial = (contact.customerName || contact.username || contact.customerPhone || '?')
    .trim()
    .charAt(0)
    .toUpperCase();
  const mutual = contact.isUserFollowBusiness && contact.isBusinessFollowUser;
  const handleName = contact.username ? `@${contact.username}` : undefined;
  const igLink = contact.username ? `https://instagram.com/${contact.username}` : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#fff',
          // The app's global MuiDialog theme override (App.tsx) puts a 1px
          // border on every dialog paper — invisible on an ordinary padded
          // dialog, but it read as a stray white strip along the top edge
          // here, where the photo sits flush against the paper with no
          // padding to hide it inside. This dialog IS the photo, so the
          // border is removed rather than worked around.
          border: 'none',
        },
      }}
    >
      {/* Large square photo, flush with the dialog edges — no card padding
          around it. This IS the photo viewer, not a decorated header band. */}
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', bgcolor: '#e2e8f0' }}>
        {contact.profilePictureUrl ? (
          <Box
            component="img"
            src={contact.profilePictureUrl}
            alt={displayName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Avatar
            variant="square"
            sx={{ width: '100%', height: '100%', bgcolor: '#4f46e5', fontSize: '4rem', fontWeight: 700 }}
          >
            {initial}
          </Avatar>
        )}
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: 'rgba(15,23,42,0.55)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(15,23,42,0.75)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {isInstagram && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              bgcolor: '#fff',
              borderRadius: '7px',
              p: '3px',
              display: 'flex',
              lineHeight: 0,
              boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
            }}
          >
            <InstagramLogo size={18} />
          </Box>
        )}
      </Box>

      {/* Info below the photo — tight spacing, no filler. */}
      <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem', minWidth: 0 }} noWrap>
            {displayName}
          </Typography>
          {contact.isVerifiedUser && <VerifiedRounded sx={{ fontSize: 17, color: '#3897f0', flexShrink: 0 }} />}
        </Stack>

        {isInstagram && handleName && (
          <Typography
            variant="body2"
            component={igLink ? 'a' : 'span'}
            {...(igLink ? { href: igLink, target: '_blank', rel: 'noopener' } : {})}
            sx={{
              color: '#4f46e5',
              fontWeight: 500,
              textDecoration: 'none',
              display: 'inline-block',
              mt: 0.25,
              '&:hover': igLink ? { textDecoration: 'underline' } : {},
            }}
          >
            {handleName}
          </Typography>
        )}

        {!isInstagram && contact.customerPhone && (
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.5 }}>
            <PhoneOutlined sx={{ fontSize: 14, color: MUTED }} />
            <Typography variant="body2" sx={{ color: MUTED }}>
              {contact.customerPhone}
            </Typography>
          </Stack>
        )}

        {/* Instagram stats + relationship — only what the enrichment actually
            returned (Advanced Access-gated), never a guessed 0/No. */}
        {isInstagram && (contact.followerCount !== undefined || contact.isUserFollowBusiness !== undefined || contact.isBusinessFollowUser !== undefined) && (
          <Stack spacing={1.25} sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${BORDER}` }}>
            {contact.followerCount !== undefined && (
              <Stack direction="row" spacing={0.75} alignItems="baseline">
                <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.95rem' }}>
                  {formatCount(contact.followerCount)}
                </Typography>
                <Typography variant="body2" sx={{ color: MUTED }}>
                  followers
                </Typography>
              </Stack>
            )}
            {mutual ? (
              <FactRow known active activeLabel="You follow each other" inactiveLabel="" icon={<HowToRegRounded sx={{ fontSize: 17 }} />} />
            ) : (
              <>
                <FactRow
                  known={contact.isUserFollowBusiness !== undefined}
                  active={contact.isUserFollowBusiness}
                  activeLabel="Follows you"
                  inactiveLabel="Doesn't follow you"
                  icon={<HowToRegRounded sx={{ fontSize: 17 }} />}
                />
                <FactRow
                  known={contact.isBusinessFollowUser !== undefined}
                  active={contact.isBusinessFollowUser}
                  activeLabel="You follow them"
                  inactiveLabel="You don't follow them"
                  icon={<PersonAddAlt1Rounded sx={{ fontSize: 17 }} />}
                />
              </>
            )}
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}
